import {mdiAlert, mdiCancel, mdiSyncOff} from '@mdi/js'
import {useTranslation} from 'react-i18next'

import {ErrorModalVariant} from '../../types/orderChangeResult.types'

export interface ErrorModalContentConfig {
  icon: string
  iconColor: string
  title: string
  description: string
  secondaryDescription: string
  primaryButtonText: string
  secondaryButtonText?: string
  secondaryButtonVariant: 'secondary' | 'negative'
  showSecondaryButton: boolean
}

/**
 * Hook to get the content configuration for the ErrorModal based on the variant.
 *
 * @param variant - The variant of the error modal
 * @param message - Optional reason from the API (e.g. a validation message). When present
 *   on a non-retryable (400) error it is shown to the user instead of the generic
 *   "technical issue" text, so an actionable reason like "Either load space or flow rate
 *   must be provided." is not hidden behind a "contact dispatch" dead end.
 * @returns The content configuration for the modal, or null if variant is null
 */
export const useErrorModalContentConfig = (
  variant: ErrorModalVariant | null,
  message?: string
): ErrorModalContentConfig | null => {
  const {t} = useTranslation()

  if (!variant) return null

  switch (variant) {
    case 'retryable':
      return {
        icon: mdiAlert,
        iconColor: 'text-amber-600',
        title: t('orderIntake.rmc.orderManagement.error.retryable.title', 'Request not sent'),
        description: t(
          'orderIntake.rmc.orderManagement.error.retryable.description',
          "We couldn't process your request due to a technical issue or a temporary connection problem."
        ),
        secondaryDescription: t(
          'orderIntake.rmc.orderManagement.error.retryable.secondaryDescription',
          'Please send it again or contact dispatch.'
        ),
        primaryButtonText: t(
          'orderIntake.rmc.orderManagement.error.retryable.primaryButton',
          'Send it again'
        ),
        secondaryButtonText: t('orderIntake.rmc.orderManagement.error.goBack', 'Go back'),
        secondaryButtonVariant: 'secondary',
        showSecondaryButton: true
      }
    case 'nonRetryable': {
      // A 400 usually carries an actionable validation reason from the API
      // (e.g. "Either load space or flow rate must be provided."). Show it so the
      // user can fix their input, instead of a generic dead-end "technical issue".
      const hasReason = Boolean(message?.trim())
      return {
        icon: mdiCancel,
        iconColor: 'text-gray-600',
        title: t('orderIntake.rmc.orderManagement.error.nonRetryable.title', 'Request failed'),
        description: hasReason
          ? (message as string)
          : t(
              'orderIntake.rmc.orderManagement.error.nonRetryable.description',
              'We were unable to process your request due to a technical issue.'
            ),
        secondaryDescription: hasReason
          ? t(
              'orderIntake.rmc.orderManagement.error.nonRetryable.adjustAndRetry',
              'Please go back, adjust your changes, and try again.'
            )
          : t(
              'orderIntake.rmc.orderManagement.error.nonRetryable.secondaryDescription',
              'Please contact dispatch for assistance.'
            ),
        primaryButtonText: t('orderIntake.rmc.orderManagement.error.goBack', 'Go back'),
        secondaryButtonVariant: 'secondary',
        showSecondaryButton: false
      }
    }
    case 'checksum':
      return {
        icon: mdiSyncOff,
        iconColor: 'text-gray-600',
        title: t(
          'orderIntake.rmc.orderManagement.error.checksum.title',
          "Request can't be processed"
        ),
        description: t(
          'orderIntake.rmc.orderManagement.error.checksum.description',
          "Some details in your request don't match the latest values we have, possibly because changes were made in another system in the meantime."
        ),
        secondaryDescription: t(
          'orderIntake.rmc.orderManagement.error.checksum.secondaryDescription',
          'Please start over and enter the changes again.'
        ),
        primaryButtonText: t(
          'orderIntake.rmc.orderManagement.error.checksum.primaryButton',
          'Start over'
        ),
        secondaryButtonText: t(
          'orderIntake.rmc.orderManagement.error.checksum.secondaryButton',
          'Discard request'
        ),
        secondaryButtonVariant: 'negative',
        showSecondaryButton: true
      }
    default:
      return {
        icon: mdiAlert,
        iconColor: 'text-red-600',
        title: t('orderIntake.rmc.orderManagement.error.default.title', 'Error'),
        description: t(
          'orderIntake.rmc.orderManagement.error.default.description',
          'An error occurred'
        ),
        secondaryDescription: '',
        primaryButtonText: t('orderIntake.rmc.orderManagement.error.goBack', 'Go back'),
        secondaryButtonVariant: 'secondary',
        showSecondaryButton: false
      }
  }
}
